/**
 * fetch wrapper that attaches the Fabric ID token.
 *
 * Copied from Fabric, as every module's is. The only per-module difference is the
 * base URL — here it points at `vault-api`, which verifies the same Fabric-issued
 * token. That is what makes one household sign-in reach every portal: every client
 * asks the same project who you are.
 */
import { getIdToken } from "./auth";

const BASE = import.meta.env.VITE_WAYPOINT_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new ApiError(401, "Not signed in");

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    // The API answers errors as {error}. Fall back to the status text when a proxy
    // or a cold start returns HTML instead, so the UI never shows
    // "Unexpected token < in JSON".
    const detail = await res
      .json()
      .then((j: { error?: string }) => j.error)
      .catch(() => null);
    throw new ApiError(res.status, detail ?? res.statusText);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  get: <T>(path: string) => call<T>("GET", path),
  post: <T>(path: string, body?: unknown) => call<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => call<T>("PATCH", path, body ?? {}),
  // PUT is the pack list's verb: it replaces whole state so an offline replay is
  // idempotent. See `offline.svelte.ts` and the route's comment.
  put: <T>(path: string, body?: unknown) => call<T>("PUT", path, body ?? {}),
  del: <T>(path: string) => call<T>("DELETE", path),
};
